import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import '../../styles/AboutScreen.css';

const AboutScreen = () => {
  const navigate = useNavigate();
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);

  const videoSources = [
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Subaru.mp4',
  ];

  const playRandomVideo = () => {
    setVideoLoaded(false);
    const randomIndex = Math.floor(Math.random() * videoSources.length);
    setCurrentVideo(videoSources[randomIndex]);
  };

  useEffect(() => { playRandomVideo(); }, []);

  /* ── Stats counter animation ── */
  const Counter = ({ end, suffix = '' }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    useEffect(() => {
      const obs = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) {
          let start = 0;
          const step = end / 60;
          const t = setInterval(() => {
            start += step;
            if (start >= end) { setCount(end); clearInterval(t); }
            else setCount(Math.floor(start));
          }, 20);
          obs.disconnect();
        }
      }, { threshold: 0.4 });
      if (ref.current) obs.observe(ref.current);
      return () => obs.disconnect();
    }, [end]);
    return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
  };

  const stats = [
    { label: 'Active Vendors',    value: 2400,  suffix: '+' },
    { label: 'Happy Customers',   value: 98000, suffix: '+' },
    { label: 'Products Listed',   value: 15000, suffix: '+' },
    { label: 'Cities Reached',    value: 120,   suffix: '+' },
  ];

  const values = [
    { icon: '🤝', title: 'Trust First',       text: 'Every vendor is vetted. Every transaction is protected. Your confidence is our foundation.' },
    { icon: '⚡', title: 'Fast Delivery',      text: 'Lightning-fast logistics network connecting sellers to buyers across every corner of the country.' },
    { icon: '🌱', title: 'Sustainable',        text: 'We actively support eco-conscious vendors and packaging to build a greener marketplace.' },
    { icon: '💡', title: 'Always Innovating',  text: 'Continuously improving the platform so you get smarter tools, better deals, and a seamless experience.' },
  ];

  return (
    <div className="ab-root">

      {/* ── Hero ── */}
      <section className="ab-hero">
        <div className="ab-hero-bg">
          <div className="ab-hero-orb ab-hero-orb1"/>
          <div className="ab-hero-orb ab-hero-orb2"/>
          <div className="ab-hero-grid"/>
        </div>
        <div className="ab-hero-body">
          <div className="ab-hero-eyebrow">Our Story</div>
          <h1 className="ab-hero-title">
            Built for <span className="ab-accent">People</span>,<br/>
            Powered by <span className="ab-accent">Purpose</span>
          </h1>
          <p className="ab-hero-sub">
            We started with a simple belief — that buying and selling online should feel human. No friction, no guesswork. Just real products from real people.
          </p>
          <button className="ab-hero-cta" onClick={() => navigate('/explore')}>
            Start Exploring <span className="ab-cta-arrow">→</span>
          </button>
        </div>
        <div className="ab-hero-scroll-hint">
          <div className="ab-scroll-dot"/>
        </div>
      </section>

      {/* ── Stats strip ── */}
      {/* <section className="ab-stats">
        <div className="ab-stats-inner">
          {stats.map((s) => (
            <div key={s.label} className="ab-stat-card">
              <div className="ab-stat-number">
                <Counter end={s.value} suffix={s.suffix}/>
              </div>
              <div className="ab-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section> */}

      {/* ── Who we are ── */}
      <section className="ab-section">
        <div className="ab-container">
          <div className="ab-two-col">
            <div className="ab-col-text">
              <div className="ab-section-tag">Who We Are</div>
              <h2 className="ab-section-heading">A marketplace built on real relationships</h2>
              <p className="ab-body-text">
                We're a team of builders, dreamers, and problem-solvers who got tired of impersonal online shopping. We built this platform so that every vendor — from a solo artisan to a growing brand — gets a fair shot at reaching customers who care.
              </p>
              <p className="ab-body-text">
                Today we serve thousands of sellers and buyers across the country, and we're just getting started. Our platform grows because our community grows — and that community is everything.
              </p>
            </div>
            <div className="ab-col-media">
              <div className="ab-video-card">
                <div className="ab-video-wrap">
                  {currentVideo ? (
                    <video
                      ref={videoRef}
                      key={currentVideo}
                      src={currentVideo}
                      controls
                      autoPlay
                      muted
                      className={`ab-video ${videoLoaded ? 'loaded' : ''}`}
                      onCanPlay={() => setVideoLoaded(true)}
                    />
                  ) : (
                    <div className="ab-video-placeholder">
                      <div className="ab-play-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <span>Loading…</span>
                    </div>
                  )}
                </div>
                <button className="ab-random-btn" onClick={playRandomVideo}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
                    <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
                  </svg>
                  Play Another
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── What we do ── */}
      <section className="ab-section ab-section-alt">
        <div className="ab-container">
          <div className="ab-center-header">
            <div className="ab-section-tag">What We Do</div>
            <h2 className="ab-section-heading">Connecting buyers and sellers — seamlessly</h2>
          </div>
          <p className="ab-body-text ab-center-text">
            We provide the tools, the trust, and the technology for vendors to showcase their products and for customers to discover exactly what they're looking for. From onboarding to checkout, every step is designed to be frictionless, safe, and genuinely enjoyable.
          </p>
          <p className="ab-body-text ab-center-text">
            Our platform handles secure payments, real-time inventory, verified reviews, and fast logistics — so vendors can focus on what they do best: making great products.
          </p>

          {/* Values grid */}
          <div className="ab-values-grid">
            {values.map((v) => (
              <div key={v.title} className="ab-value-card">
                <div className="ab-value-icon">{v.icon}</div>
                <h3 className="ab-value-title">{v.title}</h3>
                <p className="ab-value-text">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why we do it ── */}
      <section className="ab-section">
        <div className="ab-container">
          <div className="ab-why-wrap">
            <div className="ab-why-left">
              <div className="ab-section-tag">Why We Do It</div>
              <h2 className="ab-section-heading">Because commerce should empower, not complicate</h2>
            </div>
            <div className="ab-why-right">
              <p className="ab-body-text">
                We believe every person with a great product deserves an audience, and every shopper deserves to find exactly what they need without sifting through noise. Commerce should create opportunity — for vendors to grow their income, and for customers to get genuine value.
              </p>
              <p className="ab-body-text">
                That conviction drives every decision we make: the features we build, the vendors we onboard, the policies we enforce, and the experience we craft. It's not just about transactions — it's about building something that makes lives a little better.
              </p>
              <p className="ab-body-text">
                We're committed to transparency, fairness, and constant improvement. Our community holds us to a high standard — and we're grateful for it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA banner ── */}
      <section className="ab-cta-banner">
        <div className="ab-cta-bg">
          <div className="ab-cta-orb ab-cta-orb1"/>
          <div className="ab-cta-orb ab-cta-orb2"/>
        </div>
        <div className="ab-cta-body">
          <h2 className="ab-cta-title">Ready to explore?</h2>
          <p className="ab-cta-sub">Thousands of products. Verified vendors. Delivered to your door.</p>
          <button className="ab-cta-btn" onClick={() => navigate('/explore')}>
            Explore Now <span className="ab-cta-arrow">→</span>
          </button>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default AboutScreen;